import React from "react";

function splitValue(value) {
  if (!value) return [];
  return String(value).split(",").map((v) => v.trim()).filter(Boolean);
}

function joinValues(values) {
  return values.join(",");
}

function MultiSelect({ options, value, onChange, placeholder = "All", disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    function onDocClick(event) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = splitValue(value);

  function toggle(val) {
    const key = String(val);
    const next = selected.includes(key)
      ? selected.filter((v) => v !== key)
      : [...selected, key];
    onChange(joinValues(next));
  }

  function removeOne(event, val) {
    event.stopPropagation();
    onChange(joinValues(selected.filter((v) => v !== String(val))));
  }

  const selectedOptions = options.filter((o) => selected.includes(String(o.value)));
  const visibleSelectedOptions = selectedOptions.slice(0, 2);
  const hiddenSelectedCount = Math.max(0, selectedOptions.length - visibleSelectedOptions.length);
  const selectedTitle = selectedOptions.map((option) => option.label).join(", ");

  return (
    <div
      ref={wrapperRef}
      className="multi-select"
      style={{ position: "relative", width: "100%" }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) setOpen((o) => !o);
        }}
        style={{
          width: "100%",
          minHeight: 40,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 6,
          padding: selectedOptions.length ? "5px 8px" : "8px 10px",
          borderRadius: 10,
          border: "1px solid #d7dfed",
          background: disabled ? "var(--surface-sunken)" : "var(--surface)",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: 13,
        }}
        title={selectedTitle}
      >
        {selectedOptions.length === 0 ? (
          <span style={{ color: "var(--muted)" }}>{placeholder}</span>
        ) : (
          <>
            {visibleSelectedOptions.map((opt) => (
            <span
              key={opt.value}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#eff4ff",
                color: "#3b82f6",
                borderRadius: 999,
                padding: "4px 6px 4px 10px",
                fontSize: 12,
                fontWeight: 600,
                maxWidth: "100%",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {opt.label}
              </span>
              <span
                role="button"
                aria-label={`Remove ${opt.label}`}
                onClick={(e) => removeOne(e, opt.value)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "#3b82f6",
                  color: "#ffffff",
                  fontSize: 11,
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                ×
              </span>
            </span>
            ))}
            {hiddenSelectedCount ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  background: "#eef2ff",
                  color: "#3557c8",
                  borderRadius: 999,
                  padding: "4px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                +{hiddenSelectedCount} more
              </span>
            ) : null}
          </>
        )}
      </div>
      {open && !disabled && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            background: "#f8fafc",
            border: "1px solid #dbe3f0",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: 240, overflowY: "auto", padding: 8,
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: 10, color: "var(--muted)", fontSize: 12 }}>No options</div>
          ) : (
            options.map((opt) => {
              const checked = selected.includes(String(opt.value));
              return (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggle(opt.value)}
                  style={{
                    display: "block",
                    textAlign: "left",
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderRadius: 10,
                    border: checked ? "1px solid #3b61d4" : "1px solid #dbe3f0",
                    fontSize: 13,
                    background: checked ? "#4f79f0" : "#ffffff",
                    color: checked ? "#ffffff" : "var(--text)",
                    fontWeight: checked ? 600 : 400,
                    boxShadow: checked ? "0 8px 16px rgba(79, 121, 240, 0.22)" : "none",
                    transition: "background 120ms ease, border-color 120ms ease, transform 120ms ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!checked) {
                      e.currentTarget.style.background = "#f3f6fb";
                      e.currentTarget.style.borderColor = "#cfd9ea";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!checked) {
                      e.currentTarget.style.background = "#ffffff";
                      e.currentTarget.style.borderColor = "#dbe3f0";
                    }
                  }}
                >
                  {opt.label}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
